import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraCombobox, SpecraDataTable, SpecraDateField, SpecraFormField, SpecraInlineBanner, SpecraOverlayDialog, SpecraSectionHeader, SpecraText, SpecraToastMessage } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useListSeats, useMergeSeats, useUnmergeSeat } from '../hooks/useLandscapeSeats';
import { holderLabel } from './seatTime';
const T = Testids.seatAdmin;
export function useUndoSeatMerge(onError) {
    const unmerge = useUnmergeSeat();
    const pending = useRef(false);
    return async (seatId, toastId)=>{
        if (pending.current) return;
        pending.current = true;
        try {
            await unmerge.mutateAsync({
                seatId
            });
            if (toastId !== undefined) toast.dismiss(toastId);
            toast.custom((id)=>/*#__PURE__*/ _jsx(SpecraToastMessage, {
                    tone: "success",
                    title: "Merge undone",
                    onDismiss: ()=>toast.dismiss(id)
                }));
        } catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            if (onError) onError(message);
            else toast.custom((id)=>/*#__PURE__*/ _jsx(SpecraToastMessage, {
                    tone: "danger",
                    title: "Could not undo merge",
                    description: message,
                    onDismiss: ()=>toast.dismiss(id)
                }));
        } finally{
            pending.current = false;
        }
    };
}
export function MergeSeatDialog({ seat, seats, landscapeId, onClose }) {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const [date, setDate] = useState(today);
    const [survivor, setSurvivor] = useState();
    const [searchAll, setSearchAll] = useState(false);
    const [search, setSearch] = useState('');
    const [error, setError] = useState('');
    const list = useListSeats({
        search
    }, searchAll);
    const merge = useMergeSeats();
    const undo = useUndoSeatMerge();
    const candidates = (searchAll ? list.data?.seats ?? [] : seats).filter((s)=>s.id !== seat.id && !s.mergedIntoSeatId && !s.archivedAt);
    const option = (s)=>{
        const current = s.rounds.find((r)=>r.status === 'current');
        return {
            value: s.id,
            label: `${s.name} · ${current ? holderLabel(current) : '—'}`
        };
    };
    const submit = async ()=>{
        if (!survivor || merge.isPending) return;
        setError('');
        try {
            await merge.mutateAsync({
                loserSeatId: seat.id,
                survivorSeatId: survivor.id,
                mergedOn: date,
                landscapeId
            });
            onClose();
            toast.custom((id)=>/*#__PURE__*/ _jsx(SpecraToastMessage, {
                    tone: "success",
                    title: "Seats merged",
                    description: `${seat.name} now shows under ${survivor.name}.`,
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "xs",
                        variant: "secondary",
                        onClick: ()=>void undo(seat.id, id),
                        children: "Undo"
                    }),
                    onDismiss: ()=>toast.dismiss(id)
                }), {
                duration: 10000
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };
    return /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
        open: true,
        title: `Merge ${seat.name} into another seat`,
        description: `Use this when the government now buys both needs as one, for example under one integrator. ${seat.name}'s contract rounds will show under the seat you pick. You can undo this.`,
        onOpenChange: (v)=>{
            if (!v) onClose();
        },
        bodyProps: {
            className: 'space-y-4'
        },
        footerProps: {
            confirmLabel: 'Merge seats',
            cancelLabel: 'Cancel',
            onCancel: onClose,
            onConfirm: submit,
            confirmDisabled: !survivor,
            loading: merge.isPending,
            loadingLabel: 'Merging…',
            confirmProps: {
                'data-testid': T.mergeConfirm
            }
        },
        children: [
            error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                "data-testid": T.error,
                children: error
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Merge into",
                htmlFor: "merge-survivor",
                children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                    id: "merge-survivor",
                    label: "Merge into",
                    triggerProps: {
                        'data-testid': T.mergeSurvivor
                    },
                    value: survivor?.id ?? '',
                    selectedOption: survivor ? option(survivor) : undefined,
                    options: candidates.map(option),
                    onValueChange: (id)=>setSurvivor(candidates.find((s)=>s.id === id)),
                    searchValue: search,
                    onSearchValueChange: setSearch,
                    shouldFilter: !searchAll,
                    loading: searchAll && list.isFetching
                })
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                size: "xs",
                "aria-pressed": searchAll,
                onClick: ()=>{
                    setSearchAll(!searchAll);
                    setSearch('');
                    setSurvivor(undefined);
                },
                children: "Search all seats"
            }),
            searchAll && list.isError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: String(list.error)
            }),
            /*#__PURE__*/ _jsx(SpecraDateField, {
                label: "Merged on",
                picker: "native",
                value: date,
                onValueChange: setDate,
                max: today,
                "data-testid": T.mergeDate,
                hint: "When the government started buying them together."
            }),
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "What moves"
            }),
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                tableProps: {
                    className: '[&_td]:py-[var(--space-1)]'
                },
                rows: seat.rounds,
                getRowId: (r)=>r.id,
                columns: [
                    {
                        header: '#',
                        cell: (r)=>r.roundIndex
                    },
                    {
                        header: 'Status',
                        cell: (r)=>r.status
                    },
                    {
                        header: 'Holder',
                        cell: holderLabel
                    },
                    {
                        header: 'Contract',
                        cell: (r)=>r.label || r.registryContractId || '—'
                    },
                    {
                        header: 'PoP',
                        cell: (r)=>`${r.popStart || '—'} – ${r.popEnd || '—'}`
                    }
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                size: "meta",
                tone: "muted",
                children: [
                    seat.rounds.length,
                    " rounds and ",
                    seat.pursuits.length,
                    " pursuits will show under",
                    ' ',
                    survivor?.name ?? 'the seat you pick',
                    ". Nothing is deleted."
                ]
            }),
            seat.rounds.some((r)=>r.status === 'current') && survivor?.rounds.some((r)=>r.status === 'current') && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "warning",
                children: "Both seats have a current contract. After the merge, the timeline shows both, overlapping. Edit the rounds afterwards if one has ended."
            })
        ]
    });
}
